import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { Canvas, useThree } from "@react-three/fiber";
import {
  Edges,
  GizmoHelper,
  GizmoViewport,
  Grid,
  OrbitControls,
  TransformControls,
  useGLTF,
  Html,
  PerspectiveCamera,
  OrthographicCamera,
} from "@react-three/drei";

import { resolveNodeTree } from "../../engine/resolveNodeTree";
import ProfileExtrudeMesh from "../geometry/ProfileExtrudeMesh";
import FramedPanelPreview3D from "../board-editor/FramedPanelPreview3D";
import RoomWallOverlay from "../room/RoomWallOverlay";
import DrillingPreviewMesh from "../mounting-editor/DrillingPreviewMesh";
import interiorCatalogApi from "../../services/interiorCatalogApi";
import GeneratedAssetMesh from "../asset-geometry/GeneratedAssetMesh";
import {
  getViewAxes,
  vec3,
  getSectionFrame2D,
} from "../../documentation/engine/viewProjection";
import {
  buildStableFaceReferenceFromHit,
  buildVisibleFaceReferencesFromObject,
  getReferencePickDistanceMm,
} from "../../documentation/engine/dimensionReferenceUtils";


const PUBLIC_BASE_URL = "https://modularobjects.com";

const ORTHO_CAMERA_DISTANCE = 30000; // 30 m u mm
const ORTHO_CAMERA_FAR = 200000;
const PERSPECTIVE_CAMERA_NEAR = 20;
const PERSPECTIVE_CAMERA_FAR = 120000;

function applySafeCameraClipping(camera) {
  if (!camera) return;

  if (camera.isOrthographicCamera) {
    camera.near = 0.1;
    camera.far = ORTHO_CAMERA_FAR;
  } else {
    camera.near = PERSPECTIVE_CAMERA_NEAR;
    camera.far = PERSPECTIVE_CAMERA_FAR;
  }

  camera.updateProjectionMatrix?.();
}

function pushOrthographicCameraAwayFromTarget(camera, controls) {
  if (!camera?.isOrthographicCamera || !controls) return;

  const target = controls.target || new THREE.Vector3(0, 0, 0);

  let direction = camera.position.clone().sub(target);

  if (direction.lengthSq() < 1) {
    direction.set(1, 0.8, 1);
  }

  direction.normalize();

  camera.position.copy(
    target.clone().add(direction.multiplyScalar(ORTHO_CAMERA_DISTANCE))
  );

  camera.lookAt(target);
  applySafeCameraClipping(camera);
  controls.update?.();
}

function isNodeInsideSectionDepth(node, sectionPlane) {
  if (!sectionPlane) return true;

  const p = node?.worldTransform?.position || {};
  const px = Number(p.x || 0);
  const pz = Number(p.z || 0);

  const frame = getSectionFrame2D(sectionPlane);

  const sx = frame.start.x;
  const sz = frame.start.z;

  const nx = frame.depth.x;
  const nz = frame.depth.z;

  const depth = Number(sectionPlane.depth || 1000);

  const distance = (px - sx) * nx + (pz - sz) * nz;

  return distance >= 0 && distance <= depth;
}

function getAssetGlbPath(asset) {
  return (
    asset?.converted_file_path ||
    asset?.convertedFilePath ||
    asset?.converted_file ||
    asset?.convertedFile ||
    asset?.file_url ||
    ""
  );
}

function ResolvedMountingDrillings({ node, source = "component_item" }) {
  const pattern =
    source === "component_item"
      ? node.geometry?.componentItemDrillingPattern
      : node.geometry?.drillingPattern;

  const drillings =
    pattern?.drillings ||
    pattern?.holes ||
    [];

  if (!Array.isArray(drillings) || !drillings.length) return null;

  return (
    <>
      {drillings
        .filter((drill) => drill?.enabled !== false)
        .map((drill, index) => (
          <DrillingPreviewMesh
            key={
              drill.id ||
              `${node.id}_${source}_drill_${index}`
            }
            drill={drill}
          />
        ))}
    </>
  );
}

function toAbsoluteUrl(path) {
  if (!path) return "";

  const str = String(path).trim();

  // Ako je već full URL, za asset GLB skidamo pogrešni /mailserver/uploads ako postoji.
  if (str.startsWith("https://modularobjects.com/mailserver/uploads/interior/assets/")) {
    return str.replace(
      "https://modularobjects.com/mailserver/uploads/interior/assets/",
      "https://modularobjects.com/uploads/interior/assets/"
    );
  }

  if (str.startsWith("http://modularobjects.com/mailserver/uploads/interior/assets/")) {
    return str.replace(
      "http://modularobjects.com/mailserver/uploads/interior/assets/",
      "https://modularobjects.com/uploads/interior/assets/"
    );
  }

  if (str.startsWith("http")) {
    return str;
  }

  // Asset GLB fajlovi idu preko /uploads, NE preko /mailserver/uploads.
  if (str.startsWith("/mailserver/uploads/interior/assets/")) {
    return `${PUBLIC_BASE_URL}${str.replace(
      "/mailserver/uploads/interior/assets/",
      "/uploads/interior/assets/"
    )}`;
  }

  if (str.startsWith("/uploads/interior/assets/")) {
    return `${PUBLIC_BASE_URL}${str}`;
  }

  // Ako backend vrati relativno: uploads/interior/assets/...
  if (str.startsWith("uploads/interior/assets/")) {
    return `${PUBLIC_BASE_URL}/${str}`;
  }

  // fallback za ostale putanje
  if (str.startsWith("/")) {
    return `${PUBLIC_BASE_URL}${str}`;
  }

  return `${PUBLIC_BASE_URL}/${str}`;
}

function radToDeg(value) {
  return (Number(value) || 0) * (180 / Math.PI);
}

function degToRad(value) {
  return ((Number(value) || 0) * Math.PI) / 180;
}

function WorldTransformMarker({
  transform,
  label,
  color = "#ef4444",
  size = 120,
}) {
  if (!transform?.position) return null;

  const p = transform.position || {};
  const r = transform.rotation || {};

  return (
    <group
      position={[
        Number(p.x) || 0,
        Number(p.y) || 0,
        Number(p.z) || 0,
      ]}
      rotation={[
        Number(r.x) || 0,
        Number(r.y) || 0,
        Number(r.z) || 0,
      ]}
    >
      <axesHelper args={[size]} />

      <mesh>
        <sphereGeometry args={[4, 16, 16]} />
        <meshStandardMaterial color={color} />
      </mesh>

      <Html distanceFactor={28}>
  <div className="px-1.5 py-1 rounded bg-white/85 border text-[9px] whitespace-nowrap shadow-sm">
          <div className="font-semibold">{label}</div>
          <div className="font-mono text-[10px]">
            X {Number(p.x || 0).toFixed(1)} · Y {Number(p.y || 0).toFixed(1)} · Z {Number(p.z || 0).toFixed(1)}
          </div>
        </div>
      </Html>
    </group>
  );
}

function SelectedNodeCoordinateSystems({ node }) {
  if (!node) return null;

  return (
    <>
      <WorldTransformMarker
        transform={node.worldTransform}
        label="COMPONENT / NODE ORIGIN"
        color="#2563eb"
        size={150}
      />

      {node.geometryWorldTransform ? (
        <WorldTransformMarker
          transform={node.geometryWorldTransform}
          label="GLB GEOMETRY ORIGIN"
          color="#a855f7"
          size={120}
        />
      ) : null}

      {node.mountingWorldTransform ? (
        <WorldTransformMarker
          transform={node.mountingWorldTransform}
          label="MOUNTING / CNC ORIGIN"
          color="#f97316"
          size={100}
        />
      ) : null}
    </>
  );
}

function correctionPosition(correction) {
  return [
    Number(correction?.offset?.x) || 0,
    Number(correction?.offset?.y) || 0,
    Number(correction?.offset?.z) || 0,
  ];
}

function correctionRotation(correction) {
  return [
    degToRad(correction?.rotation?.x),
    degToRad(correction?.rotation?.y),
    degToRad(correction?.rotation?.z),
  ];
}

function getNodeDrillings(node) {
  const drillings =
    node?.geometry?.drillingPattern?.drillings ||
    node?.definition?.linkedMounting?.drilling_pattern_json?.drillings ||
    node?.definition?.linked_mounting?.drilling_pattern_json?.drillings ||
    [];

  return Array.isArray(drillings) ? drillings : [];
}

function evaluateFormula(value, context = {}, fallback = 0) {
  if (typeof value === "number") return value;

  const raw = String(value ?? "").trim();
  if (!raw) return fallback;

  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return numeric;

  try {
    const names = Object.keys(context || {}).filter((key) =>
      /^[A-Za-z_][A-Za-z0-9_]*$/.test(key)
    );

    const values = names.map((key) => Number(context[key]) || 0);

    const fn = new Function(...names, `"use strict"; return (${raw});`);
    const result = fn(...values);

    return Number.isFinite(Number(result)) ? Number(result) : fallback;
  } catch (err) {
    console.warn("Failed to evaluate formula:", raw, err);
    return fallback;
  }
}

function resolveValue(value, context = {}, fallback = 0) {
  return evaluateFormula(value, context, fallback);
}

function getCatalogItemIdFromNode(node) {
  return (
    node?.definition?.catalogItemId ||
    node?.definition?.catalog_item_id ||
    node?.definition?.linkedItem?.id ||
    node?.definition?.linked_item?.id ||
    node?.linkedItem?.id ||
    node?.linked_item?.id ||
    null
  );
}

function normalizeComponentMeta(component) {
  const meta = component?.metadata_json || {};

  return {
    enabled: meta.enabled !== false,
    position: {
      x: meta.position?.x ?? 0,
      y: meta.position?.y ?? 0,
      z: meta.position?.z ?? 0,
    },
    rotation: {
      x: meta.rotation?.x ?? 0,
      y: meta.rotation?.y ?? 0,
      z: meta.rotation?.z ?? 0,
    },
    parameters: meta.parameters || {},
  };
}

function normalizeComponentMounting(component) {
  const mounting =
    component?.item_mounting ||
    component?.asset_mounting ||
    {};

  const ref = mounting.reference_frame_json || {};
  const role = String(component?.asset_role || "").toLowerCase();

  let rotationSource = ref.defaultRotation || { x: 0, y: 0, z: 0 };

  if (role.includes(".right") && ref.rightRotation) {
    rotationSource = ref.rightRotation;
  }

  if (role.includes(".left") && ref.leftRotation) {
    rotationSource = ref.leftRotation;
  }

  return {
    offset: {
      x: ref.offset?.x ?? 0,
      y: ref.offset?.y ?? 0,
      z: ref.offset?.z ?? 0,
    },
    rotation: {
      x: rotationSource?.x ?? 0,
      y: rotationSource?.y ?? 0,
      z: rotationSource?.z ?? 0,
    },
    drillings:
      mounting.drilling_pattern_json?.drillings ||
      mounting.drilling_pattern_json?.holes ||
      [],
  };
}

function GlbScenePrimitive({ url, wireframe = false }) {
  const { scene } = useGLTF(url);

  const cloned = useMemo(() => {
    const obj = scene.clone(true);

    obj.position.set(0, 0, 0);
    obj.scale.set(1, 1, 1);

    obj.traverse((child) => {
      if (!child.isMesh) return;

      child.castShadow = true;
      child.receiveShadow = true;

      child.material = new THREE.MeshStandardMaterial({
        color: child.material?.color || "#cfcfcf",
        roughness: 0.55,
        metalness: 0.1,
        transparent: wireframe,
        opacity: wireframe ? 0.08 : 1,
        depthWrite: !wireframe,
        side: THREE.DoubleSide,
      });

      if (wireframe && child.geometry) {
        const edgesGeometry = new THREE.EdgesGeometry(child.geometry, 1);
        const edgesMaterial = new THREE.LineBasicMaterial({
          color: "#334155",
          transparent: true,
          opacity: 0.9,
          depthTest: true,
        });

        const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
        edges.name = "clean_edges_wireframe";
        edges.renderOrder = 2;

        child.add(edges);
      }
    });

    return obj;
  }, [scene, wireframe]);

  return <primitive object={cloned} />;
}

function getColor(node, isSelected, isHovered) {
  if (isSelected) return "#2563eb";
  if (isHovered) return "#60a5fa";

  const category = String(node?.definition?.category || "").toLowerCase();

  if (category.startsWith("board.")) return "#d1d5db";
  if (category.includes("door")) return "#d6b98c";
  if (category.includes("plinth")) return "#555555";

  return "#d1d5db";
}

function focusCameraOnNode(camera, controlsRef, node) {
  const p = node?.worldTransform?.position || { x: 0, y: 0, z: 0 };
  const target = new THREE.Vector3(p.x || 0, p.y || 0, p.z || 0);
  const offset = new THREE.Vector3(700, 500, 700);

  camera.position.copy(target.clone().add(offset));
  camera.lookAt(target);

  if (controlsRef?.current) {
    controlsRef.current.target.copy(target);
    controlsRef.current.update();
  }
}

function BoxNodeMesh({ node, isSelected, isHovered, viewMode }) {
  const g = node.geometry;

  const isRealistic = viewMode === "realistic";
const isWireframe = viewMode === "wireframe" || viewMode === "hiddenLine";
const isHiddenLine = viewMode === "hiddenLine";
const isTechnical = isWireframe || isHiddenLine;
const isCabinetBox = String(
  node?.category || node?.definition?.category || ""
).includes("cabinet.box");

  return (
    <mesh castShadow receiveShadow userData={{ nodeId: node.id }}>
      <boxGeometry args={[g.width, g.height, g.depth]} />
      <meshStandardMaterial
  color={isHiddenLine ? "#ffffff" : isRealistic ? "#ffffff" : getColor(node, isSelected, isHovered)}
  roughness={isRealistic || isHiddenLine ? 0.45 : 0.65}
  metalness={0}
  transparent={!isRealistic || isCabinetBox || isWireframe}
  opacity={
    isHiddenLine
      ? 1
      : isWireframe
        ? 0.08
        : isRealistic
          ? isCabinetBox
            ? 0.15
            : 1
          : isCabinetBox
            ? 0.5
            : isSelected
              ? 0.85
              : 0.55
  }
  depthWrite={isHiddenLine ? true : !isWireframe && isRealistic && !isCabinetBox}
/>
      {isHiddenLine ? (
  <Edges scale={1.001} color="#111827" threshold={1} />
) : isWireframe ? (
  <Edges scale={1.001} color="#334155" threshold={1} />
) : isSelected ? (
  <Edges scale={1.02} color="#2563eb" threshold={15} />
) : null}
    </mesh>
  );
}

function ProfileNodeMesh({ node, isSelected, isHovered, viewMode, materials }) {
  const g = node.geometry;
  const isWireframe = viewMode === "wireframe";
const isHiddenLine = viewMode === "hiddenLine";
const isRealistic =
  viewMode === "realistic" || viewMode === "xray";
  const isXray = viewMode === "xray";

  if (g?.profile?.type === "framed_panel") {
    return (
      <FramedPanelPreview3D
        profile={g.profile}
        parameters={node.effectiveParameters || {}}
        selected={isSelected}
      />
    );
  }

  return (
    <ProfileExtrudeMesh
  nodeId={node.id}
  profile={g.profile}
  parameters={node.effectiveParameters || {}}
  thicknessExpr={g.thicknessExpr}
  color={isRealistic ? "#ffffff" : getColor(node, isSelected, isHovered)}
  selected={isSelected}
  hovered={isHovered}
  opacity={
    isWireframe
      ? 0.45
      : isXray
        ? isSelected
          ? 0.85
          : 0.32
        : isRealistic
          ? 1
          : isSelected
            ? 1
            : 0.95
  }
  wireframe={isWireframe}
  showEdgeMarkers={!isRealistic && viewMode !== "box"}
  viewMode={viewMode}
  materialAssignments={node.materialAssignments}
  materials={materials}
  uvOrigin={node.worldTransform?.position || { x: 0, y: 0, z: 0 }}
/>
  );
}

function CatalogGlbNodeMesh({
  node,
  isSelected,
  showMountingDrillings = false,
  viewMode,
}) {
  const correction = node.mountingCorrection || {
    offset: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
  };

  const drillings = getNodeDrillings(node);
  const isWireframe = viewMode === "wireframe" || viewMode === "hiddenLine";

  return (
    <Suspense fallback={null}>
      <group>
  <group
  position={correctionPosition(correction)}
  rotation={correctionRotation(correction)}
>
  <GlbScenePrimitive
  url={node.geometry.glbUrl}
  wireframe={isWireframe}
/>
</group>

{showMountingDrillings &&
  drillings
    .filter((drill) => drill?.enabled !== false)
    .map((drill) => (
      <DrillingPreviewMesh
        key={drill.id}
        drill={drill}
      />
    ))}

  {isSelected ? <axesHelper args={[150]} /> : null}
</group>
    </Suspense>
  );
}

function ResolvedAssetGlbMesh({ node, viewMode }) {
  const glbUrl = node.geometry?.glbUrl;
  const isWireframe = viewMode === "wireframe" || viewMode === "hiddenLine";

  if (!glbUrl) return null;

  const modelOffset =
    node.geometry?.modelOffset || { x: 0, y: 0, z: 0 };

  const modelRotation =
    node.geometry?.modelRotation || { x: 0, y: 0, z: 0 };

  return (
    <group
      position={[
        Number(modelOffset.x) || 0,
        Number(modelOffset.y) || 0,
        Number(modelOffset.z) || 0,
      ]}
      rotation={[
        Number(modelRotation.x) || 0,
        Number(modelRotation.y) || 0,
        Number(modelRotation.z) || 0,
      ]}
    >
      <Suspense fallback={null}>
        <GlbScenePrimitive url={glbUrl} wireframe={isWireframe} />
      </Suspense>
    </group>
  );
}

function ResolvedGeneratedAssetMesh({ node, viewMode }) {
  const geometry = node.geometry?.generatedGeometry;
  const isWireframe = viewMode === "wireframe" || viewMode === "hiddenLine";

  if (!geometry) return null;

  const modelOffset =
    node.geometry?.modelOffset ||
    node.mountingCorrection?.offset ||
    { x: 0, y: 0, z: 0 };

  const modelRotation =
    node.geometry?.modelRotation ||
    {
      x: degToRad(node.mountingCorrection?.rotation?.x),
      y: degToRad(node.mountingCorrection?.rotation?.y),
      z: degToRad(node.mountingCorrection?.rotation?.z),
    };

  return (
    <group
      position={[
        Number(modelOffset.x) || 0,
        Number(modelOffset.y) || 0,
        Number(modelOffset.z) || 0,
      ]}
      rotation={[
        Number(modelRotation.x) || 0,
        Number(modelRotation.y) || 0,
        Number(modelRotation.z) || 0,
      ]}
    >
      <GeneratedAssetMesh
  geometry={geometry}
  wireframe={isWireframe}
/>
    </group>
  );
}

function MountingMarkerMesh({ node }) {
  return (
    <mesh>
      <sphereGeometry args={[node.geometry?.radius || 6, 16, 16]} />
      <meshStandardMaterial color="#f97316" />
    </mesh>
  );
}

function CatalogItemAssemblyComponentMesh({
  component,
  parameters = {},
  showMountingDrillings = false,
  viewMode,
}) {
  const meta = normalizeComponentMeta(component);
  const mounting = normalizeComponentMounting(component);
  const isWireframe = viewMode === "wireframe" || viewMode === "hiddenLine";

  const [assetMountingItems, setAssetMountingItems] = useState([]);

useEffect(() => {
  if (!showMountingDrillings) {
    setAssetMountingItems([]);
    return;
  }

  const mountingId = component?.asset_mounting?.id;

  if (!mountingId) {
    setAssetMountingItems([]);
    return;
  }

  let alive = true;

  async function loadAssetMountingItems() {
  try {
    const res = await interiorCatalogApi.listAssetMountingItems(mountingId);
    const items = res?.items || [];

    console.log("ASSET MOUNTING ITEMS LOADED", {
      componentId: component?.id,
      role: component?.asset_role,
      asset: component?.asset?.code,
      mountingId,
      items,
    });

    if (alive) {
      setAssetMountingItems(items);
    }
  } catch (err) {
    console.warn("load asset mounting items error:", err);

    if (alive) {
      setAssetMountingItems([]);
    }
  }
}

  loadAssetMountingItems();

  return () => {
    alive = false;
  };
}, [component?.asset_mounting?.id, showMountingDrillings]);

  if (!meta.enabled) return null;

  const asset = component.asset;
  const glbPath = getAssetGlbPath(asset);
  const glbUrl = toAbsoluteUrl(glbPath);

  if (!glbUrl) return null;

  const context = {
    ...(parameters || {}),
    ...(meta.parameters || {}),
  };

  const position = [
    resolveValue(meta.position.x, context, 0),
    resolveValue(meta.position.y, context, 0),
    resolveValue(meta.position.z, context, 0),
  ];

  const rotation = [
    degToRad(resolveValue(meta.rotation.x, context, 0)),
    degToRad(resolveValue(meta.rotation.y, context, 0)),
    degToRad(resolveValue(meta.rotation.z, context, 0)),
  ];

  const mountingOffset = [
    Number(mounting.offset.x) || 0,
    Number(mounting.offset.y) || 0,
    Number(mounting.offset.z) || 0,
  ];

  const mountingRotation = [
    degToRad(mounting.rotation.x),
    degToRad(mounting.rotation.y),
    degToRad(mounting.rotation.z),
  ];

  const drillings = Array.isArray(mounting.drillings)
    ? mounting.drillings
    : [];

  return (
  <group position={position} rotation={rotation}>
  <group position={mountingOffset} rotation={mountingRotation}>
    <Suspense fallback={null}>
      <GlbScenePrimitive url={glbUrl} wireframe={isWireframe} />
    </Suspense>
  </group>

  {showMountingDrillings
    ? assetMountingItems
        .filter((item) => item?.status !== "deleted")
        .map((item) => (
          <MountingItemMesh
            key={`asset_mounting_item_${item.id}`}
            item={item}
            parameters={{
              ...(parameters || {}),
              ...(meta.parameters || {}),
            }}
          />
        ))
    : null}

  {showMountingDrillings
    ? drillings
        .filter((drill) => drill?.enabled !== false)
        .map((drill, index) => (
          <DrillingPreviewMesh
            key={drill.id || `${component.id}_drill_${index}`}
            drill={drill}
          />
        ))
    : null}
</group>
);
}

function MountingItemMesh({ item, parameters = {}, viewMode }) {
  const asset = item?.asset || null;
  const geometry = asset?.metadata_json?.geometry || null;
  const isWireframe = viewMode === "wireframe" || viewMode === "hiddenLine";

  const positionJson = item?.position_json || { x: 0, y: 0, z: 0 };
  const rotationJson = item?.rotation_json || { x: 0, y: 0, z: 0 };
  const localParams = item?.parameters_json || {};

  const context = {
    ...(parameters || {}),
    ...(localParams || {}),
  };

  const position = [
    resolveValue(positionJson.x, context, 0),
    resolveValue(positionJson.y, context, 0),
    resolveValue(positionJson.z, context, 0),
  ];

  const rotation = [
    degToRad(resolveValue(rotationJson.x, context, 0)),
    degToRad(resolveValue(rotationJson.y, context, 0)),
    degToRad(resolveValue(rotationJson.z, context, 0)),
  ];

  const glbPath = getAssetGlbPath(asset);
  const glbUrl = toAbsoluteUrl(glbPath);

  return (
    <group position={position} rotation={rotation}>
      {geometry ? (
        <GeneratedAssetMesh geometry={geometry} wireframe={isWireframe} />
      ) : glbUrl ? (
        <Suspense fallback={null}>
          <GlbScenePrimitive url={glbUrl} wireframe={isWireframe} />
        </Suspense>
      ) : (
        <mesh>
          <sphereGeometry args={[5, 16, 16]} />
          <meshStandardMaterial color="#f97316" />
        </mesh>
      )}
    </group>
  );
}

function getItemMountingFrameTransform(itemMounting) {
  const frame = itemMounting?.reference_frame_json || {};

  return {
    offset: [
      Number(frame.offset?.x) || 0,
      Number(frame.offset?.y) || 0,
      Number(frame.offset?.z) || 0,
    ],
    rotation: [
      degToRad(frame.defaultRotation?.x),
      degToRad(frame.defaultRotation?.y),
      degToRad(frame.defaultRotation?.z),
    ],
  };
}

function CatalogItemAssemblyMesh({
  itemId,
  parameters = {},
  showMountingDrillings = false,
}) {
  const [components, setComponents] = useState([]);
const [loading, setLoading] = useState(false);
const [itemMounting, setItemMounting] = useState(null);
const [itemMountingItems, setItemMountingItems] = useState([]);

  useEffect(() => {
    if (!itemId) return;

    let alive = true;

async function loadComponents() {
  try {
    setLoading(true);

    const res = await interiorCatalogApi.listItemAssets(itemId);
    const nextComponents = res?.items || [];

    let nextItemMounting = null;
    let nextMountingItems = [];

    try {
      const mountingRes = await interiorCatalogApi.getItemMountingByItemId(itemId);
      nextItemMounting = mountingRes?.item || null;

      if (nextItemMounting?.id) {
        const miRes = await interiorCatalogApi.listItemMountingItems(
          nextItemMounting.id
        );

        nextMountingItems = miRes?.items || [];
      }
    } catch (err) {
      console.warn("load item mounting items error:", err);
    }

    if (!alive) return;

    setComponents(nextComponents);
    setItemMounting(nextItemMounting);
    setItemMountingItems(nextMountingItems);
  } catch (err) {
    console.error("load catalog item assembly components error:", err);

    if (alive) {
      setComponents([]);
      setItemMounting(null);
      setItemMountingItems([]);
    }
  } finally {
    if (alive) setLoading(false);
  }
}

    loadComponents();

    return () => {
      alive = false;
    };
  }, [itemId]);

  if (!itemId) return null;
  if (loading && !components.length) return null;

  const itemMountingFrame = getItemMountingFrameTransform(itemMounting);

return (
  <group>
    {components.map((component) => (
      <CatalogItemAssemblyComponentMesh
        key={component.id}
        component={component}
        parameters={parameters}
        showMountingDrillings={showMountingDrillings}
      />
    ))}

    {showMountingDrillings ? (
      <group
        position={itemMountingFrame.offset}
        rotation={itemMountingFrame.rotation}
      >
        {itemMountingItems
          .filter((item) => item?.status !== "deleted")
          .map((item) => (
            <MountingItemMesh
              key={`item_mounting_item_${item.id}`}
              item={item}
              parameters={parameters}
              viewMode={viewMode}
            />
          ))}
      </group>
    ) : null}
  </group>
);
}

function NodeMesh(props) {
  const { node, viewMode, materials } = props;

  if (!node.geometry) return null;

  if (node.geometry.type === "asset_glb") {
  return (
    <>
      <ResolvedAssetGlbMesh node={node} viewMode={viewMode} />

      {props.showMountingDrillings ? (
        <>
          <ResolvedMountingDrillings node={node} source="component_item" />
          <ResolvedMountingDrillings node={node} source="asset" />
        </>
      ) : null}
    </>
  );
}

 if (node.geometry.type === "generated_asset") {
  return (
    <>
      <ResolvedGeneratedAssetMesh node={node} viewMode={viewMode} />

      {props.showMountingDrillings ? (
        <>
          <ResolvedMountingDrillings node={node} source="component_item" />
          <ResolvedMountingDrillings node={node} source="asset" />
        </>
      ) : null}
    </>
  );
}

  if (node.geometry.type === "box") {
    return <BoxNodeMesh {...props} />;
  }

  if (node.geometry.type === "profile_extrude") {
  return (
    <ProfileNodeMesh
      {...props}
      viewMode={viewMode}
      materials={materials}
    />
  );
}

  if (node.geometry.type === "catalog_glb") {
    return <CatalogGlbNodeMesh {...props} />;
  }

  if (node.geometry.type === "mounting_marker") {
  return <MountingMarkerMesh node={node} />;
}

  return null;
}

function flattenTree(nodes, acc = []) {
  (nodes || []).forEach((node) => {
    acc.push(node);
    flattenTree(node.children || [], acc);
  });

  return acc;
}

function NodeWrapper({
  node,
  children,
  isSelected,
  disabled = false,
  onMultiSelect,
  onSelect,
  onResolvedSelect,
  onDoubleOpen,
  setHovered,
  controlsRef,
  registerNodeRef,
  onPickRequest,
  onFaceClick,
  toolMode,
getPartitionCandidateFromHit,
setPartitionPreview,
onDimensionHover,
onDimensionClick,
}) {
  const { camera } = useThree();
  const groupRef = useRef(null);

  const t = node.worldTransform || {};
  const p = t.position || {};
  const r = t.rotation || {};

  useEffect(() => {
    registerNodeRef?.(node.id, groupRef.current);
    return () => registerNodeRef?.(node.id, null);
  }, [node.id, registerNodeRef]);

  return (
    <group
      ref={groupRef}
      userData={{ nodeId: node.id }}   // 👈 KLJUČNO
      position={[p.x || 0, p.y || 0, p.z || 0]}
      rotation={[r.x || 0, r.y || 0, r.z || 0]}
      onPointerOver={(e) => {
        if (disabled) return;
        e.stopPropagation();
        setHovered(node.id);
        document.body.style.cursor = "pointer";
      }}
      onPointerOut={(e) => {
  if (disabled) return;
  e.stopPropagation();
  setHovered(null);
  document.body.style.cursor = "default";

  if (toolMode === "dimension") {
    onDimensionHover?.(null);
  }
}}

onPointerMove={(e) => {
  if (disabled) return;

  if (toolMode === "dimension") {
    e.stopPropagation();
    onDimensionHover?.(e);
    return;
  }

  if (toolMode !== "partition") return;

  e.stopPropagation();

  const candidate = getPartitionCandidateFromHit(e, node);
  setPartitionPreview(candidate);
}}

      onClick={(e) => {
        if (disabled) return;
  e.stopPropagation();

  if (toolMode === "dimension") {
  onDimensionClick?.(e);
  return;
}

  if (onFaceClick?.(e, node)) return;

    if (toolMode === "match") {
    onPickRequest?.(e);
    return;
  }

  const isMulti = e.ctrlKey || e.metaKey || e.shiftKey;

  if (isMulti) {
    onMultiSelect?.((prev) => {
      const exists = prev.includes(node.id);
      return exists
        ? prev.filter((id) => id !== node.id)
        : [...prev, node.id];
    });

    onSelect?.(node.id);
    onResolvedSelect?.(node);
    return;
  }

  // single select
  onMultiSelect?.([node.id]);
  onSelect?.(node.id);
  onResolvedSelect?.(node);
}}
      onDoubleClick={(e) => {
        if (disabled) return;
        e.stopPropagation();
        focusCameraOnNode(camera, controlsRef, node);
        onDoubleOpen?.(node);
      }}
    >
      {children}
      {isSelected ? <axesHelper args={[150]} /> : null}
    </group>
  );
}

function SceneTransformControls({
  selectedNode,
  selectedObject,
  transformMode,
  controlsRef,
  onTransformNode,
}) {
  if (!selectedNode || !selectedObject) return null;

  function commitTransform() {
  if (!selectedObject || !selectedNode) return;

  const worldPos = new THREE.Vector3();
  const worldQuat = new THREE.Quaternion();
  const worldScale = new THREE.Vector3();

  selectedObject.matrixWorld.decompose(worldPos, worldQuat, worldScale);

  const parentWorld = selectedNode.parentWorldTransform;

  let localMatrix;

  if (parentWorld) {
    const parentPos = new THREE.Vector3(
      parentWorld.position.x,
      parentWorld.position.y,
      parentWorld.position.z
    );

    const parentEuler = new THREE.Euler(
      parentWorld.rotation.x,
      parentWorld.rotation.y,
      parentWorld.rotation.z
    );

    const parentQuat = new THREE.Quaternion().setFromEuler(parentEuler);

    const parentMatrix = new THREE.Matrix4().compose(
      parentPos,
      parentQuat,
      new THREE.Vector3(1, 1, 1)
    );

    const parentInverse = parentMatrix.clone().invert();

    const worldMatrix = new THREE.Matrix4().compose(
      worldPos,
      worldQuat,
      new THREE.Vector3(1, 1, 1)
    );

    localMatrix = parentInverse.multiply(worldMatrix);
  } else {
    localMatrix = new THREE.Matrix4().compose(
      worldPos,
      worldQuat,
      new THREE.Vector3(1, 1, 1)
    );
  }

  const localPos = new THREE.Vector3();
  const localQuat = new THREE.Quaternion();
  const localScale = new THREE.Vector3();

  localMatrix.decompose(localPos, localQuat, localScale);

  const localEuler = new THREE.Euler().setFromQuaternion(localQuat, "XYZ");

  onTransformNode?.(selectedNode.id, {
    position: {
      x: Number(localPos.x.toFixed(3)),
      y: Number(localPos.y.toFixed(3)),
      z: Number(localPos.z.toFixed(3)),
    },
    rotation: {
      x: Number(radToDeg(localEuler.x).toFixed(3)),
      y: Number(radToDeg(localEuler.y).toFixed(3)),
      z: Number(radToDeg(localEuler.z).toFixed(3)),
    },
  });
}

 function handleChange() {
  if (!selectedObject) return;

  const worldPos = new THREE.Vector3();
  const worldQuat = new THREE.Quaternion();
  const worldScale = new THREE.Vector3();

  selectedObject.matrixWorld.decompose(worldPos, worldQuat, worldScale);

  const parentWorld = selectedNode.parentWorldTransform;

  let localMatrix;

  if (parentWorld) {
    const parentPos = new THREE.Vector3(
      parentWorld.position.x,
      parentWorld.position.y,
      parentWorld.position.z
    );

    const parentEuler = new THREE.Euler(
      parentWorld.rotation.x,
      parentWorld.rotation.y,
      parentWorld.rotation.z
    );

    const parentQuat = new THREE.Quaternion().setFromEuler(parentEuler);

    const parentMatrix = new THREE.Matrix4().compose(
      parentPos,
      parentQuat,
      new THREE.Vector3(1, 1, 1)
    );

    const parentInverse = parentMatrix.clone().invert();

    const worldMatrix = new THREE.Matrix4().compose(
      worldPos,
      worldQuat,
      new THREE.Vector3(1, 1, 1)
    );

    localMatrix = parentInverse.multiply(worldMatrix);
  } else {
    localMatrix = new THREE.Matrix4().compose(
      worldPos,
      worldQuat,
      new THREE.Vector3(1, 1, 1)
    );
  }

  const localPos = new THREE.Vector3();
  const localQuat = new THREE.Quaternion();
  const localScale = new THREE.Vector3();

  localMatrix.decompose(localPos, localQuat, localScale);

  const localEuler = new THREE.Euler().setFromQuaternion(localQuat, "XYZ");

  onTransformNode?.(selectedNode.id, {
    position: {
      x: Number(localPos.x.toFixed(3)),
      y: Number(localPos.y.toFixed(3)),
      z: Number(localPos.z.toFixed(3)),
    },
    rotation: {
      x: Number(radToDeg(localEuler.x).toFixed(3)),
      y: Number(radToDeg(localEuler.y).toFixed(3)),
      z: Number(radToDeg(localEuler.z).toFixed(3)),
    },
  });
}

  return (
    <TransformControls
  key={`${selectedNode.id}_${transformMode}`}
  object={selectedObject}
  mode={transformMode}
  space="local"
  translationSnap={null}
rotationSnap={null}
  onMouseDown={() => {
    if (controlsRef.current) controlsRef.current.enabled = false;
  }}
  onMouseUp={() => {
    commitTransform();

    if (controlsRef.current) {
      controlsRef.current.enabled = true;
      controlsRef.current.update?.();
    }
  }}
/>
  );
}

function FaceMarker({ face }) {
  if (!face?.point || !face?.normal) return null;

  const p = face.point;
  const n = face.normal;

  return (
    <group>
      <mesh position={[p.x, p.y, p.z]}>
        <sphereGeometry args={[1.0, 16, 16]} />
        <meshStandardMaterial color="#a855f7" />
      </mesh>

      <arrowHelper
        args={[
          new THREE.Vector3(n.x, n.y, n.z),
          new THREE.Vector3(p.x, p.y, p.z),
          160,
          "#a855f7",
        ]}
      />
    </group>
  );
}

function makeSegments(lines, total) {
  const sorted = [...(lines || [])]
    .map((l) => Number(l.offset || 0))
    .filter((v) => v > 0 && v < total)
    .sort((a, b) => a - b);

  const result = [];
  let prev = 0;

  sorted.forEach((offset) => {
    result.push({ from: prev, to: offset });
    prev = offset;
  });

  result.push({ from: prev, to: total });
  return result;
}

function CabinetPartitionLines({
  node,
  selectedPartition,
  onSelectPartition,
  setPartitionMenu,
  onPartitionSegmentClick,
  onPartitionLineDrag,
  partitionSide = "front",
}) {
  const category = String(node?.definition?.category || "").toLowerCase();
  if (!category.includes("cabinet.box")) return null;

  const p = node.effectiveParameters || {};
  const width = Number(p.width || 0);
  const height = Number(p.height || 0);
  const depth = Number(p.depth || 0);

  const sideWidth =
  partitionSide === "left" || partitionSide === "right" ? depth : width;

const sideHeight =
  partitionSide === "top" || partitionSide === "bottom" ? depth : height;

function getLinePosition(localX, localY) {
  const offset = 6;

  if (partitionSide === "front") {
    return [localX, localY, depth / 2 + offset];
  }

  if (partitionSide === "back") {
    return [localX, localY, -depth / 2 - offset];
  }

  if (partitionSide === "left") {
    return [-width / 2 - offset, localY, localX];
  }

  if (partitionSide === "right") {
    return [width / 2 + offset, localY, localX];
  }

  if (partitionSide === "top") {
    return [localX, height / 2 + offset, localY];
  }

  if (partitionSide === "bottom") {
    return [localX, -height / 2 - offset, localY];
  }

  return [localX, localY, depth / 2 + offset];
}

function getLineBoxArgs(horizontal, length) {
  if (partitionSide === "front" || partitionSide === "back") {
    return horizontal
      ? [length, 8, 8]
      : [8, length, 8];
  }

  if (partitionSide === "left" || partitionSide === "right") {
    return horizontal
      ? [8, 8, length]
      : [8, length, 8];
  }

  if (partitionSide === "top" || partitionSide === "bottom") {
  return horizontal
    ? [length, 8, 8]   // X pravac
    : [8, 8, length];  // Z pravac
}

  return horizontal ? [length, 8, 8] : [8, length, 8];
}

 const horizontal = node.partitions?.[partitionSide]?.horizontal || [];
const vertical = node.partitions?.[partitionSide]?.vertical || [];

  if (!horizontal.length && !vertical.length) return null;

  const rowSegments = makeSegments(horizontal, sideHeight);
const colSegments = makeSegments(vertical, sideWidth);

  return (
    <group>
      {horizontal.map((line) => {
        const y = -sideHeight / 2 + Number(line.offset || 0);
        const disabled = line.disabledSegments || [];

        return colSegments.map((col, colIndex) => {
          const key = `col_${colIndex}`;
          const isDisabled = disabled.includes(key);
const isSelected =
  selectedPartition?.cabinetId === node.id &&
  selectedPartition?.orientation === "horizontal" &&
  String(selectedPartition?.lineId) === String(line.id) &&
  selectedPartition?.segmentKey === key;

          const segWidth = col.to - col.from;
          const x = -sideWidth / 2 + col.from + segWidth / 2;

          return (
            <mesh
  key={`${line.id}_${key}`}
  position={getLinePosition(x, y)}
  onClick={(e) => {
    e.stopPropagation();
    onSelectPartition?.({
  side: partitionSide,
  cabinetId: node.id,
  orientation: "horizontal",
  lineId: line.id,
  segmentKey: key,
  offset: line.offset,
  disabled: isDisabled,
});
  }}
  onPointerDown={(e) => {
  if (e.button !== 2) return;

  e.stopPropagation();
  e.nativeEvent?.preventDefault?.();

  setPartitionMenu?.({
    x: e.nativeEvent.offsetX,
    y: e.nativeEvent.offsetY,
    cabinetId: node.id,
    orientation: "horizontal",
    lineId: line.id,
    segmentKey: key,
    disabled: isDisabled,
  });

  e.stopPropagation();
  e.nativeEvent?.preventDefault?.();

  setPartitionMenu?.({
    x: e.nativeEvent.offsetX,
y: e.nativeEvent.offsetY,
    cabinetId: node.id,
    orientation: "horizontal",
    lineId: line.id,
    segmentKey: key,
    disabled: isDisabled,
  });
}}
>
  <boxGeometry args={getLineBoxArgs(true, segWidth)} />
  <meshStandardMaterial
    color={isSelected ? "#f59e0b" : isDisabled ? "#fca5a5" : "#ff0000"}
    transparent={isDisabled}
    opacity={isDisabled ? 0.25 : 1}
    depthTest={false}
  />
</mesh>
          );
        });
      })}

      {vertical.map((line) => {
        const x = -sideWidth / 2 + Number(line.offset || 0);
        const disabled = line.disabledSegments || [];

        return rowSegments.map((row, rowIndex) => {
          const key = `row_${rowIndex}`;
          const isDisabled = disabled.includes(key);
const isSelected =
  selectedPartition?.cabinetId === node.id &&
  selectedPartition?.orientation === "vertical" &&
  String(selectedPartition?.lineId) === String(line.id) &&
  selectedPartition?.segmentKey === key;

          const segHeight = row.to - row.from;
          const y = -sideHeight / 2 + row.from + segHeight / 2;

          return (
            <mesh
  key={`${line.id}_${key}`}
  position={getLinePosition(x, y)}
  onClick={(e) => {
    e.stopPropagation();
    onSelectPartition?.({
  side: partitionSide,
  cabinetId: node.id,
  orientation: "vertical",
  lineId: line.id,
  segmentKey: key,
  offset: line.offset,
  disabled: isDisabled,
});
  }}
  onPointerDown={(e) => {
  if (e.button !== 2) return;

  e.stopPropagation();
  e.nativeEvent?.preventDefault?.();

  setPartitionMenu?.({
    x: e.nativeEvent.offsetX,
    y: e.nativeEvent.offsetY,
    cabinetId: node.id,
    orientation: "vertical",
    lineId: line.id,
    segmentKey: key,
    disabled: isDisabled,
    side: partitionSide,
  });

  e.stopPropagation();
  e.nativeEvent?.preventDefault?.();

  setPartitionMenu?.({
    x: e.nativeEvent.offsetX,
y: e.nativeEvent.offsetY,
    cabinetId: node.id,
    orientation: "vertical",
    lineId: line.id,
    segmentKey: key,
    disabled: isDisabled,
    side: partitionSide,
  });
}}
>
  <boxGeometry args={getLineBoxArgs(false, segHeight)} />
  <meshStandardMaterial
    color={isSelected ? "#f59e0b" : isDisabled ? "#fca5a5" : "#ff0000"}
    transparent={isDisabled}
    opacity={isDisabled ? 0.25 : 1}
    depthTest={false}
  />
</mesh>
          );
        });
      })}
    </group>
  );
}

function findNodeById(tree = [], id) {
  for (const node of tree || []) {
    if (String(node.id) === String(id)) return node;

    const found = findNodeById(node.children || [], id);
    if (found) return found;
  }

  return null;
}

function collectSubtreeIds(node, set = new Set()) {
  if (!node) return set;

  set.add(String(node.id));

  (node.children || []).forEach((child) => {
    collectSubtreeIds(child, set);
  });

  return set;
}

function PartitionPreviewLine({ preview, flat }) {
  if (!preview) return null;

  const node = flat.find(
    (n) => String(n.id) === String(preview.cabinetId)
  );

  if (!node) return null;

  const p = node.effectiveParameters || {};
  const width = Number(p.width || 0);
  const height = Number(p.height || 0);
  const depth = Number(p.depth || 0);

  if (!width || !height || !depth) return null;

  const side = preview.side;
  const orientation = preview.orientation;
  const offset = Number(preview.offset || 0);

  const sideWidth = side === "left" || side === "right" ? depth : width;
  const sideHeight = side === "top" || side === "bottom" ? depth : height;

  const localX = orientation === "vertical" ? -sideWidth / 2 + offset : 0;
  const localY = orientation === "horizontal" ? -sideHeight / 2 + offset : 0;

  const length = orientation === "vertical" ? sideHeight : sideWidth;

  const wt = node.worldTransform || {};
  const wp = wt.position || {};
  const wr = wt.rotation || {};

  function getPos() {
    const d = 14;

    if (side === "front") return [localX, localY, depth / 2 + d];
    if (side === "back") return [localX, localY, -depth / 2 - d];
    if (side === "left") return [-width / 2 - d, localY, localX];
    if (side === "right") return [width / 2 + d, localY, localX];
    if (side === "top") return [localX, height / 2 + d, localY];
    if (side === "bottom") return [localX, -height / 2 - d, localY];

    return [localX, localY, depth / 2 + d];
  }

  function getArgs() {
    if (side === "front" || side === "back") {
      return orientation === "horizontal"
        ? [length, 12, 12]
        : [12, length, 12];
    }

    if (side === "left" || side === "right") {
      return orientation === "horizontal"
        ? [12, 12, length]
        : [12, length, 12];
    }

    if (side === "top" || side === "bottom") {
      return orientation === "horizontal"
        ? [length, 12, 12]
        : [12, 12, length];
    }

    return [length, 12, 12];
  }

  return (
    <group
      position={[wp.x || 0, wp.y || 0, wp.z || 0]}
      rotation={[wr.x || 0, wr.y || 0, wr.z || 0]}
    >
      <mesh position={getPos()}>
        <boxGeometry args={getArgs()} />
        <meshStandardMaterial
          color="#22c55e"
          transparent
          opacity={0.9}
          depthTest={false}
        />
      </mesh>
    </group>
  );
}

function getObjectWorldCenter(object) {
  if (!object) return null;

  object.updateWorldMatrix(true, true);

  const box = new THREE.Box3().setFromObject(object);

  if (box.isEmpty()) {
    const worldPos = new THREE.Vector3();
    object.getWorldPosition(worldPos);
    return worldPos;
  }

  const center = new THREE.Vector3();
  box.getCenter(center);
  return center;
}

function fitCameraToObject(camera, controls, object, offset = 1.4) {
  if (!camera || !controls || !object) return;

  object.updateWorldMatrix(true, true);

  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return;

const boxSize = new THREE.Vector3();
const center = new THREE.Vector3();

box.getSize(boxSize);
box.getCenter(center);

  const maxSize = Math.max(boxSize.x, boxSize.y, boxSize.z);
  const distance = Math.max(maxSize * offset, 800);

  const direction = new THREE.Vector3()
    .subVectors(camera.position, controls.target)
    .normalize();

  if (!Number.isFinite(direction.x)) {
    direction.set(1, 0.8, 1).normalize();
  }

  camera.position.copy(center.clone().add(direction.multiplyScalar(distance)));

camera.near = camera.isOrthographicCamera ? -100000 : 0.1;
camera.far = Math.max(100000, distance * 20);

camera.lookAt(center);
camera.updateProjectionMatrix?.();

controls.target.copy(center);
controls.update?.();
}

function fitCameraToObjects(camera, controls, objects = [], offset = 1.5) {
  if (!camera || !controls || !objects.length) return;

  const box = new THREE.Box3();

  objects.forEach((object) => {
    if (!object) return;
    object.updateWorldMatrix(true, true);

    const objectBox = new THREE.Box3().setFromObject(object);
    if (!objectBox.isEmpty()) {
      box.union(objectBox);
    }
  });

  if (box.isEmpty()) return;

  const helper = new THREE.Object3D();
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();

  box.getSize(size);
  box.getCenter(center);

  helper.position.copy(center);

  const maxSize = Math.max(size.x, size.y, size.z);
  const distance = Math.max(maxSize * offset, 1200);

  const direction = new THREE.Vector3()
    .subVectors(camera.position, controls.target)
    .normalize();

  if (!Number.isFinite(direction.x)) {
    direction.set(1, 0.8, 1).normalize();
  }

  camera.position.copy(center.clone().add(direction.multiplyScalar(distance)));

camera.near = camera.isOrthographicCamera ? -30000 : 10;
camera.far = camera.isOrthographicCamera
  ? 30000
  : Math.max(30000, distance * 8);

camera.lookAt(center);
camera.updateProjectionMatrix?.();

controls.target.copy(center);
controls.update?.();
}

function getStandardViewCameraConfig(view, center, distance) {
  const c = center || new THREE.Vector3(0, 0, 0);
  const d = Number(distance || 3000);

  if (view === "front") {
    return {
      position: new THREE.Vector3(c.x, c.y, c.z + d),
      up: new THREE.Vector3(0, 1, 0),
    };
  }

  if (view === "back") {
    return {
      position: new THREE.Vector3(c.x, c.y, c.z - d),
      up: new THREE.Vector3(0, 1, 0),
    };
  }

  if (view === "left") {
    return {
      position: new THREE.Vector3(c.x - d, c.y, c.z),
      up: new THREE.Vector3(0, 1, 0),
    };
  }

  if (view === "right") {
    return {
      position: new THREE.Vector3(c.x + d, c.y, c.z),
      up: new THREE.Vector3(0, 1, 0),
    };
  }

  if (view === "top") {
    return {
      position: new THREE.Vector3(c.x, c.y + d, c.z),
      up: new THREE.Vector3(0, 0, -1),
    };
  }

  if (view === "bottom") {
    return {
      position: new THREE.Vector3(c.x, c.y - d, c.z),
      up: new THREE.Vector3(0, 0, 1),
    };
  }

  return {
    position: new THREE.Vector3(c.x + d, c.y + d * 0.8, c.z + d),
    up: new THREE.Vector3(0, 1, 0),
  };
}

function applyStandardView(camera, controls, view, center, distance) {
  if (!camera || !controls) return;

  const target = center || new THREE.Vector3(0, 0, 0);
  const config = getStandardViewCameraConfig(view, target, distance);

  camera.up.copy(config.up);
camera.position.copy(config.position);
camera.lookAt(target);

applySafeCameraClipping(camera);

controls.target.copy(target);

if (camera.isOrthographicCamera) {
  pushOrthographicCameraAwayFromTarget(camera, controls);
} else {
  controls.update?.();
}
}

function applyProjectionDrawingCamera({
  camera,
  controls,
  view,
  projection,
  viewportWidthMm,
  viewportHeightMm,
  scale,
}) {
  if (!camera || !camera.isOrthographicCamera) return;

  const drawingScale = Number(scale || 20);

  const visibleWorldWidthMm = Number(viewportWidthMm || 100) * drawingScale;
  const visibleWorldHeightMm = Number(viewportHeightMm || 80) * drawingScale;

  camera.left = -visibleWorldWidthMm / 2;
  camera.right = visibleWorldWidthMm / 2;
  camera.top = visibleWorldHeightMm / 2;
  camera.bottom = -visibleWorldHeightMm / 2;
  camera.zoom = 1;

  const origin = vec3(
    projection?.originWorld || { x: 0, y: 0, z: 0 }
  );

  const axes = getViewAxes(view);

  const target = new THREE.Vector3(origin.x, origin.y, origin.z);
  const n = new THREE.Vector3(axes.n.x, axes.n.y, axes.n.z).normalize();
  const v = new THREE.Vector3(axes.v.x, axes.v.y, axes.v.z).normalize();

  const distance = Math.max(
    visibleWorldWidthMm,
    visibleWorldHeightMm,
    3000
  );

  const position = target.clone().add(n.multiplyScalar(distance));

  camera.up.copy(v);
  camera.position.copy(position);
  camera.lookAt(target);

  camera.near = 1;
  camera.far = Math.max(50000, distance * 10);

  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);

  if (controls) {
  controls.target.copy(target);
  controls.update?.();
}
}

function applyExactDrawingCamera({
  camera,
  controls,
  viewType,
  target,
  viewportWidthMm,
  viewportHeightMm,
  scale,
}) {
  if (!camera || !controls || !camera.isOrthographicCamera) return;

  const drawingScale = Number(scale || 20);

  const visibleWorldWidthMm = Number(viewportWidthMm || 100) * drawingScale;
  const visibleWorldHeightMm = Number(viewportHeightMm || 80) * drawingScale;

  camera.left = -visibleWorldWidthMm / 2;
  camera.right = visibleWorldWidthMm / 2;
  camera.top = visibleWorldHeightMm / 2;
  camera.bottom = -visibleWorldHeightMm / 2;
  camera.zoom = 1;

  const distance = Math.max(visibleWorldWidthMm, visibleWorldHeightMm, 3000);
  const config = getStandardViewCameraConfig(viewType, target, distance);

  camera.up.copy(config.up);
  camera.position.copy(config.position);
  camera.lookAt(target);

  camera.near = 1;
  camera.far = Math.max(50000, distance * 10);
  camera.updateProjectionMatrix();

controls.target.copy(target);
controls.update?.();

return target;
}

function getSectionFrame(sectionPlane, fallbackCenter = new THREE.Vector3()) {
  const start = sectionPlane?.start || {
    x: -500,
    z: Number(sectionPlane?.value || 0),
  };

  const end = sectionPlane?.end || {
    x: 500,
    z: Number(sectionPlane?.value || 0),
  };

  const sx = Number(start.x || 0);
  const sz = Number(start.z ?? sectionPlane?.value ?? 0);
  const ex = Number(end.x || 0);
  const ez = Number(end.z ?? sectionPlane?.value ?? 0);

  const lineDir = new THREE.Vector3(ex - sx, 0, ez - sz);

  if (lineDir.lengthSq() < 0.0001) {
    lineDir.set(1, 0, 0);
  }

  lineDir.normalize();

  // Normal na section liniju u XZ planu.
  // Ovo mora biti isto kao u isNodeInsideSectionDepth().
 let viewDir = new THREE.Vector3(lineDir.z, 0, -lineDir.x).normalize();

if (sectionPlane?.direction === "back") {
  viewDir.multiplyScalar(-1);
}

  const planePoint = new THREE.Vector3(
    (sx + ex) / 2,
    fallbackCenter.y || 0,
    (sz + ez) / 2
  );

  return {
    planePoint,
    lineDir,
    viewDir,
  };
}

function getSectionCenteredTarget(sectionPlane, sceneBox, fallbackCenter) {
  const frame = getSectionFrame(sectionPlane, fallbackCenter);
  const { planePoint, lineDir } = frame;

  if (!sceneBox || sceneBox.isEmpty()) {
    return {
      ...frame,
      target: planePoint.clone(),
    };
  }

  const boxCenter = new THREE.Vector3();
  sceneBox.getCenter(boxCenter);

  // Uzimamo koliko je centar modela udaljen duž section linije.
  // To pomera target horizontalno levo/desno u section view-u,
  // ali NE pomera target u dubinu.
  const horizontalOffset = boxCenter.clone().sub(planePoint).dot(lineDir);

  const target = planePoint
    .clone()
    .add(lineDir.clone().multiplyScalar(horizontalOffset));

  // Y centar treba da bude centar modela po visini.
  target.y = boxCenter.y;

  return {
    ...frame,
    target,
  };
}

function applyExactSectionCamera({
  camera,
  controls,
  sectionPlane,
  sceneBox,
  fallbackCenter,
  viewportWidthMm,
  viewportHeightMm,
  scale,
}) {
  if (!camera || !controls || !camera.isOrthographicCamera || !sectionPlane) {
    return;
  }

  const drawingScale = Number(scale || 20);

  const visibleWorldWidthMm = Number(viewportWidthMm || 100) * drawingScale;
  const visibleWorldHeightMm = Number(viewportHeightMm || 80) * drawingScale;

  camera.left = -visibleWorldWidthMm / 2;
  camera.right = visibleWorldWidthMm / 2;
  camera.top = visibleWorldHeightMm / 2;
  camera.bottom = -visibleWorldHeightMm / 2;
  camera.zoom = 1;

  const { viewDir, target } = getSectionCenteredTarget(
    sectionPlane,
    sceneBox,
    fallbackCenter
  );

  const depth = Number(sectionPlane.depth || 1000);

  const cameraOffset = Math.max(
    1000,
    depth * 0.5,
    visibleWorldWidthMm
  );

  const cameraPosition = target
    .clone()
    .sub(viewDir.clone().multiplyScalar(cameraOffset));

  camera.up.set(0, 1, 0);
  camera.position.copy(cameraPosition);
  camera.lookAt(target);

  camera.near = 1;
  camera.far = Math.max(50000, cameraOffset + depth + 5000);

  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);

  controls.target.copy(target);
  controls.update?.();
}

function getFaceLabelFromNormal(normal) {
  const ax = Math.abs(normal.x);
  const ay = Math.abs(normal.y);
  const az = Math.abs(normal.z);

  if (az >= ax && az >= ay) {
    return normal.z > 0 ? "front face" : "back face";
  }

  if (ax >= ay && ax >= az) {
    return normal.x > 0 ? "right face" : "left face";
  }

  return normal.y > 0 ? "top face" : "bottom face";
}

function getViewportPointerInfoFromEvent(e, sceneViewSettings = null) {
  const viewportWidthMm =
    Number(sceneViewSettings?.viewportPaperSizeMm?.width || 100);

  const viewportHeightMm =
    Number(sceneViewSettings?.viewportPaperSizeMm?.height || 80);

  const target =
  e?.nativeEvent?.currentTarget ||
  e?.sourceEvent?.currentTarget ||
  e?.nativeEvent?.target ||
  e?.sourceEvent?.target ||
  null;

  const rect = target?.getBoundingClientRect?.();

  if (!rect) {
    return {
      pointerMm: { x: 0, y: 0 },
      pointerCssPx: { x: 0, y: 0 },
      screenPxPerMm: 3,
    };
  }

  // rect.width je REALNA veličina na ekranu, uključujući sheet zoom.
  const screenPxPerMmX = rect.width / viewportWidthMm;
  const screenPxPerMmY = rect.height / viewportHeightMm;

  const localScreenX = Number(e.nativeEvent.clientX || 0) - rect.left;
  const localScreenY = Number(e.nativeEvent.clientY || 0) - rect.top;

  const pointerMm = {
    x: localScreenX / screenPxPerMmX,
    y: localScreenY / screenPxPerMmY,
  };

  return {
    pointerMm,
    pointerCssPx: {
      x: localScreenX,
      y: localScreenY,
    },
    screenPxPerMm: (screenPxPerMmX + screenPxPerMmY) / 2,
  };
}

function buildDimensionCandidatesFromEvent(
  e,
  sceneViewSettings = null,
  size = null,
  nodeRefs = null,
  mode = "hover",
  options = {}
) {
  const candidatesById = new Map();

  if (!e) return [];

  const orientationFilter = options.orientation || null;
  const excludeRefId = options.excludeRefId || null;

  const view = {
    type:
      sceneViewSettings?.requestedProjection?.view ||
      sceneViewSettings?.requestedView?.view ||
      "front",
    sectionPlane: sceneViewSettings?.sectionPlane || null,
    projection: sceneViewSettings?.projection || null,
    scale: sceneViewSettings?.drawingScale || 20,
  };

  const viewport = {
    widthMm: Number(sceneViewSettings?.viewportPaperSizeMm?.width || 100),
    heightMm: Number(sceneViewSettings?.viewportPaperSizeMm?.height || 80),
  };

  const pointerInfo = getViewportPointerInfoFromEvent(e, sceneViewSettings);
  const pointerMm = pointerInfo.pointerMm;
  const screenPxPerMm = pointerInfo.screenPxPerMm;

  const pickRadiusPx = mode === "click" ? 26 : 70;
  const pickRadiusMm = pickRadiusPx / screenPxPerMm;

  function addCandidate(ref) {
    if (!ref?.id || !ref?.point) return;

    if (excludeRefId && String(ref.id) === String(excludeRefId)) {
      return;
    }

    if (
      orientationFilter &&
      ref.dimensionOrientation &&
      ref.dimensionOrientation !== orientationFilter
    ) {
      return;
    }

    const distanceMm = getReferencePickDistanceMm({
      ref,
      view,
      viewport,
      pointerMm,
    });

    if (distanceMm > pickRadiusMm) return;

    const candidate = {
      ...ref,
      distance: distanceMm,
      distancePx: distanceMm * screenPxPerMm,
      pointerCssPx: pointerInfo.pointerCssPx,
      pointerMm: pointerInfo.pointerMm,
    };

    const existing = candidatesById.get(candidate.id);

    if (!existing || candidate.distance < existing.distance) {
      candidatesById.set(candidate.id, candidate);
    }
  }

  // 1) Glavni profesionalni sistem:
  // uzmi sve registrovane node objekte u viewportu i napravi face reference.
  if (nodeRefs?.current) {
    nodeRefs.current.forEach((nodeObject, nodeId) => {
      if (!nodeObject) return;

      const visibleRefs = buildVisibleFaceReferencesFromObject({
        object: nodeObject,
        nodeId,
        view,
      });

      visibleRefs.forEach(addCandidate);
    });
  }

  // 2) Fallback: ako baš pogodiš konkretnu GLB / mesh površinu,
  // dodaj i stabilnu hit-based referencu.
  if (Array.isArray(e.intersections)) {
    e.intersections.forEach((hit) => {
      let obj = hit.object;
      let nodeId = null;

      while (obj) {
        if (obj.userData?.nodeId) {
          nodeId = obj.userData.nodeId;
          break;
        }

        obj = obj.parent;
      }

      if (!nodeId) return;

      const stableRef = buildStableFaceReferenceFromHit({
        hit,
        nodeId,
        view,
      });

      if (stableRef) {
        addCandidate(stableRef);
      }
    });
  }

  return Array.from(candidatesById.values())
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 30);
}

function DimensionCandidateMarker({ candidate }) {
  if (!candidate?.point) return null;

  const p = candidate.point;

  return (
    <group position={[p.x, p.y, p.z]}>
      <mesh renderOrder={999}>
        <sphereGeometry args={[8, 16, 16]} />
        <meshStandardMaterial
          color="#2563eb"
          depthTest={false}
        />
      </mesh>

      {candidate.normal ? (
        <arrowHelper
          args={[
            new THREE.Vector3(
              candidate.normal.x,
              candidate.normal.y,
              candidate.normal.z
            ),
            new THREE.Vector3(0, 0, 0),
            120,
            "#2563eb",
          ]}
        />
      ) : null}
    </group>
  );
}

function SceneContent({
  room,
  selectedRoomObjectId,
  onSelectRoomObject,
  flat,
  selectedId,
  selectedIds = [],
  onMultiSelect,
  toolMode,
  setToolMode,
  getPartitionCandidateFromHit,
  setPartitionPreview,
  hoveredId,
  setHovered,
  onSelect,
  onResolvedSelect,
  onDoubleOpen,
  controlsRef,
  transformMode,
  onTransformNode,
  onPickRequest,
  viewMode,
  isolatedNodeIds,
  onFaceClick,
  alignTarget,
  setAlignTarget,
  onPartitionSegmentClick,
  onPartitionLineDrag,
  selectedPartition,
  onSelectPartition,
  partitionMenu,
  setPartitionMenu,
  materials,
  partitionPreview,
  rootInstances,
  showMountingDrillings = false,
  showCoordinateSystems = true,
  sceneViewSettings,
  viewportMode = false,
  viewportActive = true,
  lockedCamera = false,
  onCameraChange,
  dimensionDraft,
  onDimensionPick,
  onDimensionHover,
}) {
  const { camera, size, invalidate } = useThree();
const nodeRefs = useRef(new Map());
const [refsVersion, forceTick] = useState(0);

  const settings = sceneViewSettings || {};

  const requestedViewToken = sceneViewSettings?.requestedView?.token || null;
const requestedActionToken = sceneViewSettings?.requestedAction?.token || null;

const requestedProjectionToken =
  sceneViewSettings?.requestedProjection?.token || null;

  const cameraType = settings.cameraType || "perspective";
  const showGrid = settings.showGrid !== false;
  const showGizmo = settings.showGizmo !== false;
  const showSceneCoordinateSystems = settings.showCoordinateSystems === true;

  const ambientIntensity = Number(settings.ambientIntensity ?? 0.9);
  const mainLightIntensity = Number(settings.mainLightIntensity ?? 1);

  const [dimensionCandidates, setDimensionCandidates] = useState([]);
const [dimensionCandidateIndex, setDimensionCandidateIndex] = useState(0);
const currentDimensionCandidateRef = useRef(null);

const lockedDimensionOrientation =
  dimensionDraft?.ref1?.dimensionOrientation || null;

const lockedDimensionRefId =
  dimensionDraft?.ref1?.id || null;

  const selectedNode = useMemo(
    () => flat.find((node) => String(node.id) === String(selectedId)),
    [flat, selectedId]
  );

const registerNodeRef = useCallback((nodeId, ref) => {
  const key = String(nodeId);
  const current = nodeRefs.current.get(key) || null;

  if (ref) {
    if (current === ref) return;

    nodeRefs.current.set(key, ref);
    forceTick((x) => x + 1);
    return;
  }

  if (!nodeRefs.current.has(key)) return;

  nodeRefs.current.delete(key);
  forceTick((x) => x + 1);
}, []);

  const selectedObject = selectedId
  ? nodeRefs.current.get(String(selectedId))
  : null;

 function emitCameraChange() {
  if (sceneViewSettings?.exactDrawingScale) return;
  if (!onCameraChange || !camera || !controlsRef.current) return;

  const target = controlsRef.current.target || { x: 0, y: 0, z: 0 };

  onCameraChange({
    position: {
      x: Number(camera.position.x.toFixed(3)),
      y: Number(camera.position.y.toFixed(3)),
      z: Number(camera.position.z.toFixed(3)),
    },
    target: {
      x: Number(target.x.toFixed(3)),
      y: Number(target.y.toFixed(3)),
      z: Number(target.z.toFixed(3)),
    },
    zoom: Number((camera.zoom || 1).toFixed(5)),
    fov: camera.fov ? Number(camera.fov.toFixed(3)) : null,
  });
}

useEffect(() => {
  if (toolMode !== "dimension") return;

  currentDimensionCandidateRef.current = null;
  setDimensionCandidates([]);
  setDimensionCandidateIndex(0);
  onDimensionHover?.(null);
}, [
  toolMode,
  dimensionDraft?.ref1?.id,
  onDimensionHover,
]);

useEffect(() => {
  function onKeyDown(e) {
    if (toolMode !== "dimension") return;
    if (e.key !== "Tab") return;
    if (!dimensionCandidates.length) return;

    e.preventDefault();
    e.stopPropagation();

    setDimensionCandidateIndex((prev) => {
  const nextIndex = e.shiftKey
    ? (prev - 1 + dimensionCandidates.length) % dimensionCandidates.length
    : (prev + 1) % dimensionCandidates.length;

  const candidate = dimensionCandidates[nextIndex] || null;

  if (candidate) {
    currentDimensionCandidateRef.current = candidate;

    onDimensionHover?.({
      xPx: candidate.pointerCssPx?.x ?? 0,
      yPx: candidate.pointerCssPx?.y ?? 0,
      pointerMm: candidate.pointerMm,
      candidate,
      candidates: dimensionCandidates,
    });
  }

  return nextIndex;
});
  }

  window.addEventListener("keydown", onKeyDown, true);
  return () => window.removeEventListener("keydown", onKeyDown, true);
}, [toolMode, dimensionCandidates, onDimensionHover]);

useEffect(() => {
  if (sceneViewSettings?.exactDrawingScale) return;

  const saved = sceneViewSettings?.viewCamera;

  if (!saved || !camera || !controlsRef.current) return;

  const controls = controlsRef.current;

  if (saved.position) {
    camera.position.set(
      Number(saved.position.x || 0),
      Number(saved.position.y || 0),
      Number(saved.position.z || 0)
    );
  }

  if (saved.target) {
    controls.target.set(
      Number(saved.target.x || 0),
      Number(saved.target.y || 0),
      Number(saved.target.z || 0)
    );
  }

  if (camera.isOrthographicCamera && saved.zoom) {
    camera.zoom = Number(saved.zoom || 1);
  }

  if (camera.isPerspectiveCamera && saved.fov) {
    camera.fov = Number(saved.fov || camera.fov);
  }

  camera.lookAt(controls.target);
applySafeCameraClipping(camera);

if (camera.isOrthographicCamera) {
  pushOrthographicCameraAwayFromTarget(camera, controls);
} else {
  controls.update?.();
}
}, [
  sceneViewSettings?.exactDrawingScale,
  sceneViewSettings?.viewCamera,
  camera,
  controlsRef,
]);

useLayoutEffect(() => {
  const request = sceneViewSettings?.requestedProjection;

  if (!request?.view || !camera) return;
if (!sceneViewSettings?.exactDrawingScale) return;

applyProjectionDrawingCamera({
  camera,
  controls: controlsRef.current || null,
  view: {
    type: request.view,
    sectionPlane: sceneViewSettings?.sectionPlane || null,
  },
  projection: sceneViewSettings?.projection || {
    originWorld: { x: 0, y: 0, z: 0 },
  },
  viewportWidthMm:
    sceneViewSettings?.viewportPaperSizeMm?.width || 100,
  viewportHeightMm:
    sceneViewSettings?.viewportPaperSizeMm?.height || 80,
  scale: sceneViewSettings?.drawingScale || 20,
});

invalidate?.();

});

useEffect(() => {
  const request = sceneViewSettings?.requestedView;
  if (!request?.view || !camera || !controlsRef.current) return;

  if (sceneViewSettings?.exactDrawingScale) return;

  const controls = controlsRef.current;
  const target = controls.target?.clone?.() || new THREE.Vector3(0, 0, 0);

  applyStandardView(
  camera,
  controls,
  request.view,
  target,
  camera.isOrthographicCamera ? ORTHO_CAMERA_DISTANCE : 3000
);
}, [
  requestedViewToken,
  camera,
  controlsRef,
  sceneViewSettings?.exactDrawingScale,
]);

useEffect(() => {
  const action = sceneViewSettings?.requestedAction;
  if (!action?.type || !camera || !controlsRef.current) return;

  if (sceneViewSettings?.exactDrawingScale) return;

  const controls = controlsRef.current;

  if (action.type === "resetView") {
    const target = new THREE.Vector3(0, 0, 0);

    camera.position.set(2200, 1800, 2200);
    camera.lookAt(target);
    camera.updateProjectionMatrix?.();

    controls.target.copy(target);
    controls.update?.();
    return;
  }

  if (action.type === "fitSelected") {
    if (!selectedObject) return;
    fitCameraToObject(camera, controls, selectedObject, 1.5);
    return;
  }

  if (action.type === "fitAll") {
    const objects = Array.from(nodeRefs.current.values()).filter(Boolean);
    fitCameraToObjects(camera, controls, objects, 1.6);
  }
}, [
  requestedActionToken,
  camera,
  controlsRef,
  selectedObject,
  refsVersion,
]);

useEffect(() => {
  if (sceneViewSettings?.disableAutoFocusSelected) return;
  if (!selectedObject || !controlsRef.current) return;

  const center = getObjectWorldCenter(selectedObject);
  if (!center) return;

  controlsRef.current.target.copy(center);
  controlsRef.current.update();
}, [
  selectedObject,
  selectedId,
  controlsRef,
  sceneViewSettings?.disableAutoFocusSelected,
]);

  return (
    <>
      <color attach="background" args={["#f3f4f6"]} />

      {cameraType === "orthographic" ? (
  <OrthographicCamera
  makeDefault
  position={[
    ORTHO_CAMERA_DISTANCE,
    ORTHO_CAMERA_DISTANCE * 0.8,
    ORTHO_CAMERA_DISTANCE,
  ]}
  zoom={0.35}
  near={0.1}
  far={ORTHO_CAMERA_FAR}
/>
) : (
  <PerspectiveCamera
  makeDefault
  position={[2200, 1800, 2200]}
  fov={35}
  near={20}
  far={50000}
/>
)}

      <ambientLight intensity={ambientIntensity} />

<directionalLight
  position={[3000, 4000, 2000]}
  intensity={mainLightIntensity}
  castShadow
/>

<directionalLight
  position={[-3000, 2000, -2000]}
  intensity={mainLightIntensity * 0.3}
/>

      <RoomWallOverlay
  room={room}
  selectedRoomObjectId={selectedRoomObjectId}
  onSelectRoomObject={onSelectRoomObject}
  toolMode={toolMode}
  onWallFaceClick={(payload) => {
  if (toolMode !== "align") return false;

  // Zid je target površina.
  // Ako je prvi klik, zapamti zid kao target.
  if (!alignTarget) {
    setAlignTarget(payload);
    return true;
  }

  // Ako korisnik opet klikne zid, samo zameni target.
  // Element koji se pomera mora biti drugi klik u handleFaceClick.
  setAlignTarget(payload);
  return true;
}}
/>

      {showGrid ? (
  <Grid
    args={[10000, 10000]}
    cellSize={50}
    sectionSize={500}
    fadeDistance={50000}
  />
) : null}

      {showSceneCoordinateSystems ? (
  <>
    <WorldTransformMarker
      transform={{
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
      }}
      label="GLOBAL ORIGIN 0,0,0"
      color="#ef4444"
      size={180}
    />

    <SelectedNodeCoordinateSystems node={selectedNode} />
  </>
) : null}

      {flat
  .filter((node) => {
    if (
      isolatedNodeIds &&
      !isolatedNodeIds.has(String(node.id))
    ) {
      return false;
    }

    if (
      sceneViewSettings?.sectionPlane &&
      !isNodeInsideSectionDepth(node, sceneViewSettings.sectionPlane)
    ) {
      return false;
    }

    const category = String(node?.definition?.category || "").toLowerCase();

    const isBox = category.includes("cabinet.box");
    const isBoard = category.startsWith("board");

    if (viewMode === "box") {
      return isBox;
    }

    if (viewMode === "boards") {
      return isBoard;
    }

    if (viewMode === "full") {
      return !isBox;
    }

    if (viewMode === "realistic") {
      return !isBox;
    }

    if (viewMode === "xray") {
      return !isBox;
    }

    if (viewMode === "wireframe" || viewMode === "hiddenLine") {
      return true;
    }

    return true;
  })
  .map((node) => {
        const isSelected =
  selectedIds.map(String).includes(String(node.id)) ||
  String(selectedId) === String(node.id);
        const isHovered = String(hoveredId) === String(node.id);

        return (
          <NodeWrapper
  key={node.id}
  node={node}
  isSelected={isSelected}
  disabled={viewportMode && !viewportActive}
  onMultiSelect={onMultiSelect}
  onSelect={onSelect}
  onResolvedSelect={onResolvedSelect}
  onDoubleOpen={onDoubleOpen}
  setHovered={setHovered}
  controlsRef={controlsRef}
  registerNodeRef={registerNodeRef}
  onPickRequest={onPickRequest}
  onFaceClick={onFaceClick}
  toolMode={toolMode}
  getPartitionCandidateFromHit={getPartitionCandidateFromHit}
  setPartitionPreview={setPartitionPreview}
  onDimensionHover={(e) => {
  if (!e) {
  currentDimensionCandidateRef.current = null;
  setDimensionCandidates([]);
  setDimensionCandidateIndex(0);
  onDimensionHover?.(null);
  return;
}

const next = buildDimensionCandidatesFromEvent(
  e,
  sceneViewSettings,
  size,
  nodeRefs,
  "hover",
  {
    orientation: lockedDimensionOrientation,
    excludeRefId: lockedDimensionRefId,
  }
);

  setDimensionCandidates(next);

  setDimensionCandidateIndex((prev) => {
    const safeIndex = next.length ? Math.min(prev, next.length - 1) : 0;
    const candidate = next[safeIndex] || null;

    if (candidate) {
  currentDimensionCandidateRef.current = candidate;

  onDimensionHover?.({
    xPx: candidate.pointerCssPx?.x ?? 0,
    yPx: candidate.pointerCssPx?.y ?? 0,
    pointerMm: candidate.pointerMm,
    candidate,
    candidates: next,
  });
} else {
  currentDimensionCandidateRef.current = null;
  onDimensionHover?.(null);
}

    return safeIndex;
  });
}}
onDimensionClick={(e) => {
  const hoveredCandidate = currentDimensionCandidateRef.current;

if (hoveredCandidate) {
  if (
    lockedDimensionOrientation &&
    hoveredCandidate.dimensionOrientation !== lockedDimensionOrientation
  ) {
    return;
  }

  onDimensionPick?.({
    candidate: hoveredCandidate,
    candidates: dimensionCandidates,
    candidateIndex: dimensionCandidateIndex,
  });

  currentDimensionCandidateRef.current = null;
  onDimensionHover?.(null);
  return;
}

  const next = buildDimensionCandidatesFromEvent(
  e,
  sceneViewSettings,
  size,
  nodeRefs,
  "click",
  {
    orientation: lockedDimensionOrientation,
    excludeRefId: lockedDimensionRefId,
  }
);

  if (!next.length) return;

  const index = Math.min(dimensionCandidateIndex, next.length - 1);
  const candidate = next[index];

  onDimensionPick?.({
    candidate,
    candidates: next,
    candidateIndex: index,
  });

  currentDimensionCandidateRef.current = null;
  onDimensionHover?.(null);
}}
>
            <NodeMesh
  node={node}
  isSelected={isSelected}
  isHovered={isHovered}
  viewMode={viewMode}
  materials={materials}
  showMountingDrillings={showMountingDrillings}
/>
{toolMode === "partition" &&
  ["front", "back", "left", "right", "top", "bottom"].map((side) => (
    <CabinetPartitionLines
      key={`partition_${node.id}_${side}`}
      node={node}
      selectedPartition={selectedPartition}
      onSelectPartition={onSelectPartition}
      setPartitionMenu={setPartitionMenu}
      onPartitionSegmentClick={onPartitionSegmentClick}
      onPartitionLineDrag={onPartitionLineDrag}
      partitionSide={side}
    />
  ))}
          </NodeWrapper>
        );
      })}

      <PartitionPreviewLine
  preview={partitionPreview}
  flat={flat}
/>

      {viewportActive && !sceneViewSettings?.exactDrawingScale ? (
  <SceneTransformControls
    selectedNode={selectedNode}
    selectedObject={selectedObject}
    transformMode={transformMode}
    controlsRef={controlsRef}
    onTransformNode={onTransformNode}
  />
) : null}
      <FaceMarker face={alignTarget} />

      {toolMode === "dimension" &&
!sceneViewSettings?.exactDrawingScale &&
dimensionCandidates.length ? (
  <DimensionCandidateMarker
    candidate={
      dimensionCandidates[
        Math.min(dimensionCandidateIndex, dimensionCandidates.length - 1)
      ]
    }
  />
) : null}

      <OrbitControls
  ref={controlsRef}
  makeDefault
  enabled={
  viewportActive &&
  !sceneViewSettings?.exactDrawingScale
}
  enableRotate={
    viewportActive &&
    sceneViewSettings?.allowRotate !== false &&
    !lockedCamera
  }
  enablePan={
  viewportActive &&
  sceneViewSettings?.allowPan !== false &&
  !sceneViewSettings?.exactDrawingScale
}
  enableZoom={
  viewportActive &&
  sceneViewSettings?.allowZoom !== false &&
  !sceneViewSettings?.exactDrawingScale
}
  enableDamping={false}
  dampingFactor={0}
  rotateSpeed={1.0}
  zoomSpeed={3.0}
  panSpeed={1.0}
  minDistance={50}
  maxDistance={80000}
  onEnd={emitCameraChange}
/>

      {showGizmo && viewportActive ? (
  <GizmoHelper alignment="bottom-right">
    <GizmoViewport />
  </GizmoHelper>
) : null}
    </>
  );
}

const TOOL_HINTS = {
  select: "Select elements. Ctrl/Shift click for multi-select.",
  move: "Move selected element with gizmo.",
  rotate: "Rotate selected element with gizmo.",
  align: "First click target face or wall, then click the element face to move.",
  match: "Click source element, then target element.",
  partition: "Click cabinet face to add split. Press Tab to switch split direction.",
  dimension: "Hover a face to pick a dimension reference. Press Tab to cycle nearby references.",
  view: "Double click this viewport to activate the view.",
};

export default function HierarchySceneCanvas({
  rootInstances = [],
  definitions = [],
  globalParameters = {},
  room,
  selectedRoomObjectId,
  onSelectRoomObject,
  selectedId,
  selectedIds = [],
  isolatedNodeId,
  onMultiSelect,
  toolMode = "select",
  setToolMode,
  viewMode = "realistic",
  materials = [],
  setViewMode,
  selectedPartition,
  onSelectPartition,
  onSelect,
  onResolvedSelect,
  onDoubleOpen,
  onTransformNode,
  onChangeNode,
  showMountingDrillings = false,
  showCoordinateSystems = true,
  sceneViewSettings,
  onSceneViewSettingsChange,
  viewportMode = false,
  viewportActive = true,
  lockedCamera = false,
  renderDpr,
  onCameraChange,
  dimensionDraft,
  onDimensionPick,
  onDimensionHover,
}) {
  const [hoveredId, setHovered] = useState(null);
  const [transformMode, setTransformMode] = useState("translate");
  const controlsRef = useRef(null);
const [alignTarget, setAlignTarget] = useState(null);

const [matchSourceNode, setMatchSourceNode] = useState(null);

  const [pickCandidates, setPickCandidates] = useState([]);
const [pickScreenPos, setPickScreenPos] = useState(null);

const [sceneHovered, setSceneHovered] = useState(false);
const [pickIndex, setPickIndex] = useState(0);
const lastPickIdsRef = useRef([]);

const [partitionMenu, setPartitionMenu] = useState(null);

const [partitionPreview, setPartitionPreview] = useState(null);

const [partitionPreviewOrientation, setPartitionPreviewOrientation] = useState(null);

  const resolved = useMemo(
    () =>
      resolveNodeTree({
        rootInstances,
        definitions,
        globalParameters,
      }),
    [rootInstances, definitions, globalParameters]
  );

  const flat = useMemo(() => flattenTree(resolved), [resolved]);

  const isolatedNodeIds = useMemo(() => {
  if (!isolatedNodeId) return null;

  const root = findNodeById(resolved, isolatedNodeId);

  if (!root) return null;

  return collectSubtreeIds(root);
}, [resolved, isolatedNodeId]);

 const sceneKey = useMemo(() => {
  return JSON.stringify({
    viewMode,
    rootIds: rootInstances.map((x) => x.id),
    count: flat.length,
  });
}, [viewMode, rootInstances, flat.length]);

  function getNodeIdsFromEvent(e) {
  const ids = [];

  (e.intersections || []).forEach((hit) => {
    let obj = hit.object;

    while (obj) {
      if (obj.userData?.nodeId) {
        ids.push(obj.userData.nodeId);
        break;
      }
      obj = obj.parent;
    }
  });

  return [...new Set(ids)];
}

function handlePartitionSegmentClick(payload) {
  const cabinet = rootInstances.find(
    (n) => String(n.id) === String(payload.cabinetId)
  );

  if (!cabinet) return;

  const side = payload.side || "front";

  const partitions = cabinet.partitions || {};
  const activePartition = partitions[side] || {
    horizontal: [],
    vertical: [],
    panelOverrides: {},
  };

  const listKey =
    payload.orientation === "horizontal" ? "horizontal" : "vertical";

  const nextLines = (activePartition[listKey] || []).map((line) => {
    if (String(line.id) !== String(payload.lineId)) return line;

    const current = line.disabledSegments || [];
    const exists = current.includes(payload.segmentKey);

    return {
      ...line,
      disabledSegments: exists
        ? current.filter((x) => x !== payload.segmentKey)
        : [...current, payload.segmentKey],
    };
  });

  onChangeNode?.({
    id: payload.cabinetId,
    partitions: {
      ...partitions,
      [side]: {
        ...activePartition,
        [listKey]: nextLines,
      },
    },
  });
}

function detectBoxSideFromNormal(normal) {
  const ax = Math.abs(normal.x);
  const ay = Math.abs(normal.y);
  const az = Math.abs(normal.z);

  if (az >= ax && az >= ay) return normal.z > 0 ? "front" : "back";
  if (ax >= ay && ax >= az) return normal.x > 0 ? "right" : "left";
  return normal.y > 0 ? "top" : "bottom";
}

function getPartitionCandidateFromHit(e, node) {
  const category = String(node?.category || node?.definition?.category || "").toLowerCase();
  if (!category.includes("cabinet.box")) return null;

  const hit = e.intersections?.[0];
  if (!hit?.face || !hit?.point) return null;

  const normal = hit.face.normal.clone();
  normal.transformDirection(hit.object.matrixWorld).normalize();

  const side = detectBoxSideFromNormal(normal);

  const p = node.effectiveParameters || {};
  const width = Number(p.width || 0);
  const height = Number(p.height || 0);
  const depth = Number(p.depth || 0);

  const localPoint = hit.point.clone();
  hit.object.worldToLocal(localPoint);

  let verticalOffset = 0;
let horizontalOffset = 0;
let autoOrientation = "vertical";

if (side === "front" || side === "back") {
  verticalOffset = localPoint.x + width / 2;
  horizontalOffset = localPoint.y + height / 2;
}

if (side === "left" || side === "right") {
  verticalOffset = localPoint.z + depth / 2;
  horizontalOffset = localPoint.y + height / 2;
}

if (side === "top" || side === "bottom") {
  verticalOffset = localPoint.x + width / 2;
  horizontalOffset = localPoint.z + depth / 2;
}

const sideWidth =
  side === "left" || side === "right" ? depth : width;

const sideHeight =
  side === "top" || side === "bottom" ? depth : height;

const distToVertical = Math.min(verticalOffset, sideWidth - verticalOffset);
const distToHorizontal = Math.min(horizontalOffset, sideHeight - horizontalOffset);

autoOrientation =
  distToVertical < distToHorizontal ? "vertical" : "horizontal";

const orientation = partitionPreviewOrientation || autoOrientation;

const rawOffset =
  orientation === "vertical" ? verticalOffset : horizontalOffset;

const offset = Math.round(rawOffset / 10) * 10;

if (offset <= 0) return null;

return {
  cabinetId: node.id,
  side,
  orientation,
  autoOrientation,
  offset,
};
}

function addPartitionLine(candidate) {
  if (!candidate) return;

  const cabinet = rootInstances.find(
    (n) => String(n.id) === String(candidate.cabinetId)
  );

  if (!cabinet) return;

  const partitions = cabinet.partitions || {};
  const activePartition = partitions[candidate.side] || {
    horizontal: [],
    vertical: [],
    panelOverrides: {},
  };

  const listKey = candidate.orientation === "horizontal" ? "horizontal" : "vertical";

  onChangeNode?.({
    id: candidate.cabinetId,
    partitions: {
      ...partitions,
      [candidate.side]: {
        ...activePartition,
        [listKey]: [
          ...(activePartition[listKey] || []),
          {
            id: Date.now(),
            offset: candidate.offset,
          },
        ],
      },
    },
  });
}

function getHitFaceCenter(hit) {
  const geometry = hit.object.geometry;
  const pos = geometry?.attributes?.position;
  if (!pos || !hit.face) return hit.point.clone();

  const a = new THREE.Vector3().fromBufferAttribute(pos, hit.face.a);
  const b = new THREE.Vector3().fromBufferAttribute(pos, hit.face.b);
  const c = new THREE.Vector3().fromBufferAttribute(pos, hit.face.c);

  const center = a.add(b).add(c).divideScalar(3);
  hit.object.localToWorld(center);

  return center;
}

function handleFaceClick(e, node) {

if (toolMode === "partition") {
  const candidate = getPartitionCandidateFromHit(e, node);
  if (candidate) {
    addPartitionLine(candidate);
    setPartitionPreview(null);
  }
  return true;
}

  if (toolMode !== "align") return false;

  const hit = e.intersections?.[0];
  if (!hit?.face || !hit?.point) return true;

  const normal = hit.face.normal.clone();
  normal.transformDirection(hit.object.matrixWorld).normalize();

  const center = getHitFaceCenter(hit);

  const data = {
    nodeId: node.id,
    center,
    point: center,
    normal,
  };

  if (!alignTarget) {
  // PRVI KLIK = target površina
  setAlignTarget(data);
  setHovered(node.id);
  return true;
}

// DRUGI KLIK = source površina, odnosno element koji pomeramo
const target = alignTarget;
const source = data;

const sourceNode = flat.find((n) => String(n.id) === String(source.nodeId));

if (!sourceNode) {
  setAlignTarget(null);
  return true;
}

const currentWorldPos =
  sourceNode?.worldTransform?.position || { x: 0, y: 0, z: 0 };

const deltaFull = target.center.clone().sub(source.center);

// Koristimo normalu TARGET površine.
// Time pomeramo source površinu do target površine.
const n = target.normal.clone().normalize();

const distance = deltaFull.dot(n);
const delta = n.multiplyScalar(distance);

const nextWorldPos = {
  x: Number((currentWorldPos.x + delta.x).toFixed(3)),
  y: Number((currentWorldPos.y + delta.y).toFixed(3)),
  z: Number((currentWorldPos.z + delta.z).toFixed(3)),
};

onTransformNode?.(source.nodeId, {
  position: nextWorldPos,
  rotation: sourceNode?.transform?.rotation || { x: 0, y: 0, z: 0 },
});

setAlignTarget(null);
setToolMode?.("select");
return true;
}

function handlePickRequest(e) {
  const unique = getNodeIdsFromEvent(e);
  lastPickIdsRef.current = unique;

  if (!unique.length) return;

  const pickedId = unique[0];
const pickedNode = flat.find((n) => String(n.id) === String(pickedId));

if (toolMode === "match") {
  if (!matchSourceNode) {
    setMatchSourceNode(pickedNode);
    setHovered(pickedId);
    onSelect?.(pickedId);
    onResolvedSelect?.(pickedNode);
    return;
  }

  if (String(matchSourceNode.id) === String(pickedNode.id)) return;

  if (!canMatchProperties(matchSourceNode, pickedNode)) {
    alert("Elementi nisu iste vrste/klase.");
    return;
  }

  onChangeNode?.(buildMatchedPayload(matchSourceNode, pickedNode));

  onSelect?.(pickedNode.id);
  onResolvedSelect?.(pickedNode);

  setMatchSourceNode(null);
  setToolMode("select");
  return;
}

 if (unique.length === 1) {
  const node = flat.find((n) => String(n.id) === String(unique[0]));

  onSelect?.(unique[0]);
  onResolvedSelect?.(node);
  setHovered(unique[0]);
  setPickCandidates([]);
  setPickScreenPos(null);
  return;
}

  setPickCandidates(unique);
  setPickIndex(0);
  setHovered(unique[0]);
  setPickScreenPos({
    x: e.nativeEvent?.offsetX ?? e.clientX,
  y: e.nativeEvent?.offsetY ?? e.clientY,
  });
}

function canMatchProperties(source, target) {
  if (!source || !target) return false;

  const sourceCategory = String(source.definition?.category || source.category || "");
  const targetCategory = String(target.definition?.category || target.category || "");

  const sourceNodeType = String(source.definition?.nodeType || source.nodeType || "");
  const targetNodeType = String(target.definition?.nodeType || target.nodeType || "");

  return sourceCategory === targetCategory && sourceNodeType === targetNodeType;
}

function cloneNodeOverrides(sourceNode) {
  return {
    parameterOverrides: JSON.parse(
      JSON.stringify(sourceNode.parameterOverrides || {})
    ),
    partitions: sourceNode.partitions
      ? JSON.parse(JSON.stringify(sourceNode.partitions))
      : undefined,
    children: (sourceNode.children || []).map((child) => ({
      id: child.id,
      overrideKey: child.overrideKey,
      parameterOverrides: JSON.parse(
        JSON.stringify(child.parameterOverrides || {})
      ),
    })),
  };
}

function deepClone(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function pickEffectiveOverrides(source) {
  const result = {
    ...(source.parameterOverrides || {}),
  };

  const effective = source.effectiveParameters || {};

  ["width", "height", "depth", "thickness", "boardThickness", "backThickness"].forEach(
    (key) => {
      if (effective[key] !== undefined) {
        result[key] = effective[key];
      }
    }
  );

  return result;
}

function buildChildDefinitionOverrides(source) {
  const fixedRoles = ["left", "right", "top", "bottom", "back"];

  const fromSource =
    source.parentChildDefinitionOverrides &&
    Object.keys(source.parentChildDefinitionOverrides).length
      ? deepClone(source.parentChildDefinitionOverrides)
      : {};

  const result = { ...fromSource };

  (source.children || []).forEach((child) => {
    const key = String(child.overrideKey || "");

    if (!fixedRoles.includes(key)) return;
    if (result[key]) return;

    result[key] = {
      definitionId: String(child.definitionId),
    };
  });

  return result;
}

function buildMatchedPayload(source, target) {
  const childOverrides = buildChildDefinitionOverrides(source);

  return {
    id: target.id,

    parameterOverrides: pickEffectiveOverrides(source),

    partitions: deepClone(source.partitions || null),

    childDefinitionOverrides: childOverrides,
    parentChildDefinitionOverrides: childOverrides,
  };
}

function handlePartitionLineDrag(payload) {
  const cabinet = rootInstances.find(
    (n) => String(n.id) === String(payload.cabinetId)
  );

  if (!cabinet) return;

  const partitions = cabinet.partitions || {
    front: { horizontal: [], vertical: [] },
  };

  const listKey =
    payload.orientation === "horizontal" ? "horizontal" : "vertical";

  const nextLines = (partitions.front[listKey] || []).map((line) =>
    String(line.id) === String(payload.lineId)
      ? { ...line, offset: Math.max(1, Math.round(payload.offset)) }
      : line
  );

  onChangeNode?.({
    id: payload.cabinetId,
    partitions: {
      ...partitions,
      front: {
        ...partitions.front,
        [listKey]: nextLines,
      },
    },
  });
}

useEffect(() => {
  function handleKey(e) {
    if (e.key === "Escape") {
      setPickCandidates([]);
      setPickScreenPos(null);
      setPartitionMenu(null);
    }
  }

  window.addEventListener("keydown", handleKey);
  return () => window.removeEventListener("keydown", handleKey);
}, []);

useEffect(() => {
  if (toolMode !== "partition") {
    setPartitionPreview(null);
    setPartitionPreviewOrientation(null);
  }
}, [toolMode]);

useEffect(() => {
  function onKeyDown(e) {
    if (viewportMode && !viewportActive) return;

    const tag = document.activeElement?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return;

    if (e.key.toLowerCase() === "a") {
      e.preventDefault();
      setToolMode?.("align");
      setAlignTarget(null);
      return;
    }

    if (e.key.toLowerCase() === "m") {
      setToolMode?.("move");
      setTransformMode("translate");
      setAlignTarget(null);
      return;
    }

    if (e.key.toLowerCase() === "r") {
      setToolMode?.("rotate");
      setTransformMode("rotate");
      setAlignTarget(null);
      return;
    }

    if (e.key === "Escape") {
      setToolMode?.("select");
      setAlignTarget(null);
    }
  }

  window.addEventListener("keydown", onKeyDown);
  return () => window.removeEventListener("keydown", onKeyDown);
}, [viewportMode, viewportActive, setToolMode]);

useEffect(() => {
  function onKeyDown(e) {
    if (toolMode !== "partition") return;
    if (e.key !== "Tab") return;

    e.preventDefault();
    e.stopPropagation();

    setPartitionPreviewOrientation((prev) =>
      prev === "horizontal" ? "vertical" : "horizontal"
    );
  }

  window.addEventListener("keydown", onKeyDown, true);
  return () => window.removeEventListener("keydown", onKeyDown, true);
}, [toolMode]);

useEffect(() => {
  function onKeyDown(e) {
    if (!sceneHovered) return;
    if (e.key !== "Tab") return;

    const ids = pickCandidates.length ? pickCandidates : lastPickIdsRef.current;
    if (!ids.length) return;

    e.preventDefault();
    e.stopPropagation();

    const nextIndex = e.shiftKey
      ? (pickIndex - 1 + ids.length) % ids.length
      : (pickIndex + 1) % ids.length;

    setPickCandidates(ids);
    setPickIndex(nextIndex);
    setHovered(ids[nextIndex]);
    onSelect?.(ids[nextIndex]);
  }

  window.addEventListener("keydown", onKeyDown, true);
  return () => window.removeEventListener("keydown", onKeyDown, true);
}, [sceneHovered, pickCandidates, pickIndex, onSelect]);

  return (
    <div
  className="relative w-full h-full bg-gray-100"
  onMouseEnter={() => setSceneHovered(true)}
  onMouseLeave={() => setSceneHovered(false)}
  onContextMenu={(e) => e.preventDefault()}
>
   

     <Canvas
  frameloop={viewportMode ? "demand" : "always"}
  resize={{ scroll: false }}
  gl={{
    outputColorSpace: THREE.SRGBColorSpace,
    toneMapping: THREE.ACESFilmicToneMapping,
    toneMappingExposure: 1.25,
  }}
  shadows
  dpr={viewportMode ? renderDpr || 2 : [1, 2]}
  onPointerMissed={() => {
  setPickCandidates([]);
  setPickScreenPos(null);
  setPartitionMenu(null);

  if (toolMode === "dimension") {
    onDimensionHover?.(null);
  }
}}
>
        <SceneContent
          room={room}
          selectedRoomObjectId={selectedRoomObjectId}
          onSelectRoomObject={onSelectRoomObject}
          flat={flat}
          selectedId={selectedId}
          selectedIds={selectedIds}
          onMultiSelect={onMultiSelect}
          toolMode={toolMode}
          setToolMode={setToolMode}
          getPartitionCandidateFromHit={getPartitionCandidateFromHit}
          setPartitionPreview={setPartitionPreview}
          hoveredId={hoveredId}
          setHovered={setHovered}
          onSelect={onSelect}
          onResolvedSelect={onResolvedSelect}
          onDoubleOpen={onDoubleOpen}
          controlsRef={controlsRef}
          transformMode={transformMode}
          onTransformNode={onTransformNode}
          onPickRequest={handlePickRequest}
          viewMode={viewMode}
          isolatedNodeIds={isolatedNodeIds}
          onFaceClick={handleFaceClick}
          alignTarget={alignTarget}
          setAlignTarget={setAlignTarget}
          onPartitionSegmentClick={handlePartitionSegmentClick}
          onPartitionLineDrag={handlePartitionLineDrag}
          selectedPartition={selectedPartition}
          onSelectPartition={onSelectPartition}
          partitionMenu={partitionMenu}
          setPartitionMenu={setPartitionMenu}
          materials={materials}
          partitionPreview={partitionPreview}
          rootInstances={rootInstances}
          showMountingDrillings={showMountingDrillings}
          showCoordinateSystems={showCoordinateSystems}
          sceneViewSettings={sceneViewSettings}
          viewportMode={viewportMode}
          viewportActive={viewportActive}
          lockedCamera={lockedCamera}
          onCameraChange={onCameraChange}
          dimensionDraft={dimensionDraft}
          onDimensionPick={onDimensionPick}
          onDimensionHover={onDimensionHover}
        />
      </Canvas>

      {toolMode && (!viewportMode || viewportActive) && (
  <div className="absolute left-3 top-3 z-20 pointer-events-none">
    <div className="rounded-lg border bg-white/90 shadow-sm px-3 py-2 max-w-[360px]">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {toolMode}
      </div>
      <div className="text-xs text-slate-600">
        {TOOL_HINTS[toolMode] || "Editor tool active."}
      </div>
    </div>
  </div>
)}

      {pickCandidates.length > 1 && pickScreenPos && (
  <div
    style={{
      position: "absolute",
      top: pickScreenPos.y,
      left: pickScreenPos.x,
      background: "white",
      border: "1px solid #ccc",
      borderRadius: 6,
      padding: 6,
      zIndex: 20,
      minWidth: 160,
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    }}
  >
    {pickCandidates.map((id) => {
      const node = flat.find((n) => String(n.id) === String(id));

      return (
        <div
          key={id}
          style={{
  padding: "6px 8px",
  cursor: "pointer",
  borderRadius: 4,
  background: pickCandidates[pickIndex] === id ? "#dbeafe" : "white",
}}
          onMouseEnter={() => setHovered(id)}
          onMouseLeave={() => setHovered(null)}
          onClick={() => {
            onSelect?.(id);
onResolvedSelect?.(node);
setPickCandidates([]);
setPickScreenPos(null);
          }}
        >
          {node?.name || node?.definition?.name || "Element"}
        </div>
      );
    })}
  </div>
)}

{partitionMenu && (
  <div
    style={{
      position: "absolute",
      top: partitionMenu.y,
      left: partitionMenu.x,
      zIndex: 30,
      background: "white",
      border: "1px solid #ddd",
      borderRadius: 8,
      boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
      padding: 6,
      minWidth: 160,
    }}
  >
    <button
      type="button"
      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded"
      onClick={() => {
        handlePartitionSegmentClick(partitionMenu);
        setPartitionMenu(null);
      }}
    >
      {partitionMenu.disabled ? "Restore segment" : "Hide segment"}
    </button>

    <button
      type="button"
      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded"
      onClick={() => setPartitionMenu(null)}
    >
      Cancel
    </button>
  </div>
)}
    </div>
  );
}